/**
 * ChatBubble - Modern Chat Message Component
 * 
 * Features:
 * - Clean, minimal design inspired by modern chat apps
 * - Tool calls display (Copilot-style)
 * - Inline actions on hover
 * - Smooth animations
 * - Markdown rendering
 */

import {
    AiFillPauseCircle,
    AiOutlineLoading3Quarters,
    BiCopy,
    BiEdit,
    BiRedo,
    BiX,
    BsTrash,
    toast,
    FaCheck,
    FaTimes,
    FaSearch,
    FaGlobe,
    FaCalculator,
    FaClock,
    FaCode,
    FaBrain,
    FaExternalLinkAlt,
    FaCog,
    MdExpandMore,
    MdExpandLess,
    BsRobot,
    BsPerson,
} from "@acme/ui"
import { type ChatMessage, generateChatContentString, type ChatMessageTextContent, dayjs, type ToolCall, type ToolCallStatus } from "@acme/util"
import { useAtom, useSetAtom } from "jotai"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { chatRegenerateMessageAtom, chatRemoveMessageAtom, chatResolvePendingToolAtom, chatUpdateMessageAtom } from "../store/chat"
import dynamic from "next/dynamic"
import { useTextToSpeech } from "../hooks/useTextToSpeech"
import { textToSpeechSettingsAtom } from "../store/setting"

type Props = {
    id: string
    sessionId: string
    isMe?: boolean
    role?: ChatMessage['senderRole']
    name?: string
    pic?: string
    created?: Date
    updated?: Date
    isGenerating?: boolean
    isLoading?: boolean
    message: ChatMessageTextContent
    messageCustoms?: ChatMessage['contentCustoms']
    loadingText?: string
    toolCalls?: ToolCall[]
    abortCtrl?: AbortController | null
    /** Reasoning/thinking content from reasoning models */
    thinking?: string
    /** Whether the model is currently in thinking phase */
    isThinking?: boolean
}

const TextMarkDown = dynamic(() => import("@acme/ui").then(a => a.TextMarkDown), { ssr: false })

export const ChatBubble = memo(({
    id,
    sessionId,
    isMe: _isMe = false,
    updated,
    role = 'user',
    name: _name = '',
    pic: _pic = '',
    created,
    message,
    isLoading = false,
    isGenerating = false,
    loadingText = '',
    messageCustoms,
    toolCalls,
    abortCtrl,
    thinking,
    isThinking = false,
}: Props) => {
    const onDeleteMessage = useSetAtom(chatRemoveMessageAtom)
    const onUpdateMessage = useSetAtom(chatUpdateMessageAtom)
    const onRegenerateMessage = useSetAtom(chatRegenerateMessageAtom)
    const onResolvePendingTool = useSetAtom(chatResolvePendingToolAtom)
    const [isEdit, setIsEdit] = useState(false)
    const [contentTemp, setContentTemp] = useState<string>(
        Array.isArray(message.content) ? message.content.join('\n') || '' : message.content || ''
    )

    const messageFixed = useMemo(() => {
        return generateChatContentString(message)
    }, [message])

    const onCopy = useCallback(() => {
        void navigator.clipboard.writeText(messageFixed)
        toast.success('Copied to clipboard')
    }, [messageFixed])

    const onDelete = useCallback(() => {
        onDeleteMessage(id, sessionId || '')
    }, [id, sessionId, onDeleteMessage])

    const onSave = useCallback((value: string, isGenerate?: boolean, contentCustoms?: ChatMessage['contentCustoms']) => {
        onUpdateMessage({ id, content: { content: value, contentType: 'text' }, ...contentCustoms ? { contentCustoms } : {} })
        setIsEdit(false)
        if (isGenerate) {
            void onRegenerateMessage({ sessionId, messageId: id, messageContent: { contentType: 'text', content: value } })
        }
    }, [id, sessionId, onUpdateMessage, onRegenerateMessage])

    const onCancel = useCallback(() => {
        setIsEdit(false)
        if (abortCtrl?.abort) {
            abortCtrl.abort()
        }
        onUpdateMessage({ id, loading: false, generating: false }, { isSkip: true })
        if (!message?.content && id) {
            onDeleteMessage(id, sessionId, true)
        }
    }, [abortCtrl, id, message, onDeleteMessage, onUpdateMessage, sessionId])

    const messageAgo = useMemo(() => {
        if (!created) return { date: '' }
        if (created && updated) {
            if (dayjs(updated).isAfter(created)) {
                return { date: dayjs(updated).fromNow(), isUpdated: true }
            }
            return { date: dayjs(created).fromNow() }
        }
        return { date: dayjs(created).fromNow() }
    }, [created, updated])

    useEffect(() => {
        if (isEdit) {
            setContentTemp(Array.isArray(message.content) ? message.content.join('\n') || '' : message.content || '')
        }
    }, [isEdit, message])

    // System message - minimal display
    if (role === 'system') {
        const systemMessageLimit = messageFixed.length > 200 ? messageFixed.substring(0, 200) + '...' : messageFixed
        return (
            <div id={id} className="py-2 px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center py-2 px-4 bg-neutral-100 dark:bg-neutral-800/80 ring-1 ring-neutral-200/70 dark:ring-neutral-700/70 rounded-full text-xs text-neutral-500 dark:text-neutral-400">
                        {systemMessageLimit}
                    </div>
                </div>
            </div>
        )
    }

    const isUser = role === 'user'

    return (
        <div id={id} className="group py-4 px-4 transition-colors hover:bg-neutral-50/70 dark:hover:bg-white/[0.02]">
            <div className="max-w-3xl mx-auto">
                <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Avatar — squared-off with a subtle ring reads more current
                        than a flat circle, and the ring keeps it legible on both themes. */}
                    <div className={`
                        flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center
                        ring-1 shadow-sm
                        ${isUser
                            ? 'bg-gradient-to-br from-blue-500 to-blue-600 ring-blue-600/20'
                            : 'bg-gradient-to-br from-neutral-700 to-neutral-900 ring-black/10 dark:from-neutral-600 dark:to-neutral-700 dark:ring-white/10'
                        }
                    `}>
                        {isUser ? (
                            <BsPerson className="text-white text-sm" />
                        ) : (
                            <BsRobot className="text-white text-sm" />
                        )}
                    </div>

                    {/* Message content */}
                    <div className={`flex-1 min-w-0 ${isUser ? 'flex flex-col items-end' : ''}`}>
                        {/* Name & time header */}
                        <div className={`flex items-center gap-2 mb-1.5 ${isUser ? 'flex-row-reverse' : ''}`}>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {isUser ? 'You' : 'Assistant'}
                            </span>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                {messageAgo?.isUpdated ? 'edited ' : ''}{messageAgo?.date}
                            </span>
                        </div>

                        {/* Message bubble. Rings instead of borders so the bubble
                            edge stays crisp at any zoom and doesn't add layout width. */}
                        <div className={`
                            relative max-w-[85%] rounded-2xl px-4 py-3
                            ${isUser
                                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-md shadow-sm shadow-blue-500/20'
                                : 'bg-white dark:bg-neutral-800/80 ring-1 ring-neutral-200/90 dark:ring-neutral-700/90 rounded-bl-md shadow-sm shadow-black/[0.03]'
                            }
                        `}>
                            {isLoading && !isThinking ? (
                                <div className="flex items-center gap-3 py-1">
                                    <div className="flex gap-1">
                                        <span className="w-2 h-2 bg-current rounded-full animate-bounce opacity-60" style={{ animationDelay: '0ms' }} />
                                        <span className="w-2 h-2 bg-current rounded-full animate-bounce opacity-60" style={{ animationDelay: '150ms' }} />
                                        <span className="w-2 h-2 bg-current rounded-full animate-bounce opacity-60" style={{ animationDelay: '300ms' }} />
                                    </div>
                                    <span className={`text-sm ${isUser ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                        {loadingText || 'Thinking...'}
                                    </span>
                                </div>
                            ) : isEdit ? (
                                <ChatBubbleEdit
                                    role={role}
                                    value={contentTemp}
                                    valueCustoms={messageCustoms}
                                    setIsEdit={setIsEdit}
                                    onSave={onSave}
                                />
                            ) : (
                                <div className={isUser ? 'text-white' : 'text-gray-800 dark:text-gray-100'}>
                                    {/* Tool calls display */}
                                    {toolCalls && toolCalls.length > 0 && (
                                        <ToolCallsDisplay
                                            sessionId={sessionId}
                                            messageId={id}
                                            toolCalls={toolCalls}
                                            onResolvePendingTool={async (toolCall, decision) => {
                                                await onResolvePendingTool({
                                                    sessionId,
                                                    toolCall,
                                                    decision,
                                                })
                                            }}
                                        />
                                    )}

                                    {/* Thinking/Reasoning section */}
                                    {thinking && (
                                        <ThinkingSection
                                            thinking={thinking}
                                            isThinking={isThinking}
                                        />
                                    )}

                                    {/* Active thinking indicator (before any text arrives) */}
                                    {isThinking && !messageFixed && (
                                        <div className="flex items-center gap-2 py-1 text-sm text-gray-500 dark:text-gray-400">
                                            <div className="flex gap-1">
                                                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                            <span className="text-xs italic">Thinking...</span>
                                        </div>
                                    )}

                                    {/* Message text */}
                                    {messageFixed ? (
                                        <TextMarkDown className={`prose prose-sm max-w-none ${isUser ? 'prose-invert' : 'dark:prose-invert'}`}>
                                            {messageFixed}
                                        </TextMarkDown>
                                    ) : !isLoading && !isGenerating && !isThinking && !isUser ? (
                                        <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 italic">
                                            <span>No response received.</span>
                                            <button
                                                onClick={() => void onRegenerateMessage({ sessionId, messageId: id })}
                                                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline not-italic"
                                            >
                                                Regenerate
                                            </button>
                                        </div>
                                    ) : null}

                                    {/* Image attachments */}
                                    <ChatMessageCustomPreview messageCustoms={messageCustoms} />
                                </div>
                            )}
                        </div>

                        {/* Actions bar - always visible for assistant, hover for user */}
                        {!isEdit && !isLoading && (
                            <div className={`
                                flex items-center gap-1 mt-2 transition-opacity
                                ${isUser
                                    ? 'flex-row-reverse opacity-0 group-hover:opacity-100'
                                    : 'opacity-60 group-hover:opacity-100'
                                }
                            `}>
                                <ActionButton icon={<BiCopy />} onClick={onCopy} tooltip="Copy" />
                                <ActionButton icon={<BiEdit />} onClick={() => setIsEdit(true)} tooltip="Edit" />
                                {!isUser && (
                                    <ActionButton icon={<BiRedo />} onClick={() => void onRegenerateMessage({ sessionId, messageId: id })} tooltip="Regenerate" />
                                )}
                                <ActionButton icon={<BsTrash />} onClick={onDelete} tooltip="Delete" variant="danger" />
                            </div>
                        )}

                        {/* Cancel button during generation */}
                        {(isLoading || isGenerating) && (
                            <button
                                onClick={onCancel}
                                className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                            >
                                <BiX className="text-sm" />
                                Stop generating
                            </button>
                        )}

                        {/* TTS for assistant messages */}
                        {!isUser && !isEdit && !isLoading && (
                            <ChatBubbleTextToSpeech text={messageFixed} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
})

ChatBubble.displayName = 'ChatBubble'

// =============================================================================
// ACTION BUTTON
// =============================================================================

const ActionButton = memo(({
    icon,
    onClick,
    tooltip,
    variant = 'default'
}: {
    icon: React.ReactNode
    onClick: () => void
    tooltip: string
    variant?: 'default' | 'danger'
}) => (
    <button
        onClick={onClick}
        title={tooltip}
        className={`
            p-1.5 rounded-md text-sm transition-colors
            ${variant === 'danger'
                ? 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700'
            }
        `}
    >
        {icon}
    </button>
))

ActionButton.displayName = 'ActionButton'

// =============================================================================
// IMAGE PREVIEW
// =============================================================================

const ChatMessageCustomPreview = memo(({ messageCustoms }: { messageCustoms: ChatMessage['contentCustoms'] }) => {
    const images = useMemo(() => {
        return messageCustoms?.filter(custom => custom.content.image_url?.url) || []
    }, [messageCustoms])

    if (images.length === 0) return null

    return (
        <div className="flex flex-wrap gap-2 mt-3">
            {images.map((custom, index) => (
                <a
                    key={index}
                    href={custom.content.image_url?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={custom.content.image_url?.url}
                        alt={`Attachment ${index + 1}`}
                        className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-gray-200 dark:border-neutral-600 hover:opacity-90 transition-opacity"
                    />
                </a>
            ))}
        </div>
    )
})

ChatMessageCustomPreview.displayName = 'ChatMessageCustomPreview'

// =============================================================================
// EDIT MODE
// =============================================================================

const ChatBubbleEdit = memo(({
    value,
    valueCustoms,
    setIsEdit,
    onSave,
    role
}: {
    value: string | string[] | null
    valueCustoms?: ChatMessage['contentCustoms']
    role: ChatMessage['senderRole']
    setIsEdit: (val: boolean) => void
    onSave: (value: string, isGenerate?: boolean, contentCustoms?: ChatMessage['contentCustoms']) => void
}) => {
    const [loaded, setLoaded] = useState(false)
    const [contentTemp, setContentTemp] = useState(Array.isArray(value) ? value.join('\n') : value)
    const [valueCustomsTemp] = useState<ChatMessage['contentCustoms']>(valueCustoms)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    const onSaveFixed = useCallback((isGenerate?: boolean) => {
        if (valueCustomsTemp) {
            onSave(contentTemp || '', isGenerate, valueCustomsTemp)
        } else {
            onSave(contentTemp || '', isGenerate)
        }
    }, [contentTemp, onSave, valueCustomsTemp])

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'inherit'
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`
        }
    }, [contentTemp])

    useEffect(() => {
        if (inputRef.current && !loaded) {
            inputRef.current.focus()
            inputRef.current.selectionStart = inputRef.current.selectionEnd = inputRef.current.value.length
            setLoaded(true)
        }
    }, [loaded])

    return (
        <div className="w-full min-w-[300px]">
            <textarea
                ref={inputRef}
                value={contentTemp || ''}
                onChange={(e) => setContentTemp(e.target.value)}
                className="w-full bg-transparent resize-none outline-none text-sm min-h-[60px]"
                placeholder="Edit message..."
            />

            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-neutral-700">
                <button
                    onClick={() => onSaveFixed(false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                >
                    <FaCheck className="text-[10px]" />
                    Save
                </button>
                {role === 'user' && (
                    <button
                        onClick={() => onSaveFixed(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                    >
                        <BiRedo className="text-sm" />
                        Save & Regenerate
                    </button>
                )}
                <button
                    onClick={() => setIsEdit(false)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    )
})

ChatBubbleEdit.displayName = 'ChatBubbleEdit'

// =============================================================================
// THINKING/REASONING SECTION
// =============================================================================

/**
 * Collapsible thinking/reasoning display — similar to ChatGPT, Claude, and Copilot Chat.
 * Shows the model's chain-of-thought in a collapsible accordion.
 * Auto-expands while thinking, auto-collapses when done.
 */
const ThinkingSection = memo(({
    thinking,
    isThinking,
}: {
    thinking: string
    isThinking: boolean
}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [wasManuallyToggled, setWasManuallyToggled] = useState(false)
    const thinkingRef = useRef<HTMLDivElement>(null)
    const scrollRafRef = useRef<number | null>(null)

    // Auto-expand while thinking, auto-collapse when done
    // But respect user's manual toggle
    useEffect(() => {
        if (isThinking && !wasManuallyToggled) {
            setIsExpanded(true)
        } else if (!isThinking && !wasManuallyToggled) {
            setIsExpanded(false)
        }
    }, [isThinking, wasManuallyToggled])

    // Reset manual toggle tracking when thinking starts fresh
    useEffect(() => {
        if (isThinking) {
            setWasManuallyToggled(false)
        }
    }, [isThinking])

    // Throttled auto-scroll using rAF (avoids layout thrashing during fast streaming)
    useEffect(() => {
        if (!isThinking || !isExpanded || !thinkingRef.current) return
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = requestAnimationFrame(() => {
            if (thinkingRef.current) {
                thinkingRef.current.scrollTop = thinkingRef.current.scrollHeight
            }
        })
        return () => {
            if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current)
        }
    }, [thinking, isThinking, isExpanded])

    const handleToggle = useCallback(() => {
        setWasManuallyToggled(true)
        setIsExpanded(prev => !prev)
    }, [])

    // Preview: first meaningful line, only computed when collapsed
    const thinkingPreview = useMemo(() => {
        if (isExpanded || !thinking) return ''
        const firstLine = thinking.split('\n').find(l => l.trim()) || ''
        return firstLine.length > 60 ? firstLine.substring(0, 60) + '…' : firstLine
    }, [thinking, isExpanded])

    // Word count for finished thoughts (more meaningful than token estimate)
    const wordCount = useMemo(() => {
        if (isThinking || !thinking) return ''
        const words = thinking.trim().split(/\s+/).length
        if (words < 20) return ''
        return `${words} words`
    }, [thinking, isThinking])

    return (
        <div className={`mb-3 rounded-xl border overflow-hidden transition-all duration-300 ${isThinking
            ? 'border-purple-200/80 dark:border-purple-800/60 bg-gradient-to-b from-purple-50/60 to-white dark:from-purple-950/20 dark:to-neutral-900/50 shadow-sm shadow-purple-100/50 dark:shadow-purple-900/20'
            : 'border-gray-200/80 dark:border-neutral-700/80 bg-gray-50/30 dark:bg-neutral-900/30'
            }`}>
            {/* Header — always visible */}
            <button
                onClick={handleToggle}
                className={`w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-left ${isExpanded
                    ? 'hover:bg-gray-50/50 dark:hover:bg-neutral-800/30'
                    : 'hover:bg-gray-100/50 dark:hover:bg-neutral-800/50'
                    }`}
            >
                {/* Icon */}
                <div className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-colors duration-300 ${isThinking
                    ? 'bg-purple-100 dark:bg-purple-900/40'
                    : 'bg-gray-100 dark:bg-neutral-800'
                    }`}>
                    {isThinking ? (
                        <svg className="w-3 h-3 text-purple-500 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="42" strokeDashoffset="12" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg className="w-3 h-3 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </div>

                {/* Label */}
                <span className={`text-xs font-medium transition-colors duration-300 ${isThinking
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-500 dark:text-gray-400'
                    }`}>
                    {isThinking ? 'Thinking…' : 'Thought process'}
                </span>

                {/* Collapsed: preview text */}
                {!isExpanded && thinkingPreview && (
                    <span className="flex-1 min-w-0 text-[11px] text-gray-400 dark:text-gray-500 truncate">
                        {thinkingPreview}
                    </span>
                )}

                {/* Right side: word count + chevron */}
                <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
                    {wordCount && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {wordCount}
                        </span>
                    )}
                    <MdExpandMore className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Expandable content — uses CSS grid trick for smooth height animation */}
            <div
                className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
            >
                <div className="overflow-hidden">
                    <div
                        ref={thinkingRef}
                        className={`px-3 pb-3 pt-1.5 border-t max-h-[300px] overflow-y-auto ${isThinking
                            ? 'border-purple-100/80 dark:border-purple-900/40'
                            : 'border-gray-100 dark:border-neutral-800'
                            }`}
                    >
                        <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                            <TextMarkDown className="prose prose-xs max-w-none dark:prose-invert prose-p:my-1 prose-p:leading-relaxed">
                                {thinking}
                            </TextMarkDown>
                            {isThinking && (
                                <span className="inline-block w-1.5 h-3.5 bg-purple-400 dark:bg-purple-500 animate-pulse ml-0.5 -mb-0.5 rounded-sm" />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})

ThinkingSection.displayName = 'ThinkingSection'

// =============================================================================
// TOOL CALLS DISPLAY
// =============================================================================

const TOOL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
    webSearch: FaSearch,
    webScraper: FaGlobe,
    apiCall: FaExternalLinkAlt,
    calculator: FaCalculator,
    dateTime: FaClock,
    codeExecute: FaCode,
    memory: FaBrain,
}

const TOOL_LABELS: Record<string, string> = {
    webSearch: 'Searched the web',
    webScraper: 'Fetched URL content',
    apiCall: 'Made API request',
    calculator: 'Calculated',
    dateTime: 'Checked date/time',
    codeExecute: 'Executed code',
    memory: 'Used memory',
}

const ToolCallStatusIcon = memo(({ status }: { status: ToolCallStatus }) => {
    switch (status) {
        case 'pending':
            return <FaClock className="text-amber-500 w-3 h-3" />
        case 'running':
            return <AiOutlineLoading3Quarters className="animate-spin text-blue-500 w-3 h-3" />
        case 'completed':
            return <FaCheck className="text-green-500 w-3 h-3" />
        case 'error':
            return <FaTimes className="text-red-500 w-3 h-3" />
        default:
            return null
    }
})

ToolCallStatusIcon.displayName = 'ToolCallStatusIcon'

const ToolCallItem = memo(({
    toolCall,
    onResolve,
}: {
    toolCall: ToolCall
    onResolve?: (toolCall: ToolCall, decision: 'approve' | 'reject') => Promise<void>
}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isResolving, setIsResolving] = useState(false)
    const Icon = TOOL_ICONS[toolCall.name] || FaCog
    const label = TOOL_LABELS[toolCall.name] || `Used ${toolCall.name}`

    const formatDuration = (ms?: number) => {
        if (!ms) return ''
        if (ms < 1000) return `${ms}ms`
        return `${(ms / 1000).toFixed(1)}s`
    }

    const formatResult = (result: unknown): string => {
        if (result === undefined || result === null) return ''
        if (typeof result === 'string') {
            return result.length > 300 ? result.substring(0, 300) + '...' : result
        }
        try {
            const str = JSON.stringify(result, null, 2)
            return str.length > 300 ? str.substring(0, 300) + '...' : str
        } catch {
            return String(result)
        }
    }

    const pendingMessage = useMemo(() => {
        if (toolCall.status !== 'pending') return ''
        if (toolCall.error) return toolCall.error
        if (toolCall.result && typeof toolCall.result === 'object') {
            const maybeMessage = (toolCall.result as { message?: unknown }).message
            if (typeof maybeMessage === 'string') return maybeMessage
        }
        return 'Awaiting your confirmation before continuing.'
    }, [toolCall.status, toolCall.error, toolCall.result])

    const handleResolve = useCallback(async (decision: 'approve' | 'reject') => {
        if (!onResolve || isResolving) return
        setIsResolving(true)
        try {
            await onResolve(toolCall, decision)
        } finally {
            setIsResolving(false)
        }
    }, [onResolve, toolCall, isResolving])

    return (
        <div className="rounded-lg border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors text-left"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-6 h-6 rounded bg-gray-200 dark:bg-neutral-700 flex items-center justify-center">
                        <Icon className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                        {label}
                    </span>
                    {toolCall.duration && (
                        <span className="text-[10px] text-gray-400">
                            {formatDuration(toolCall.duration)}
                        </span>
                    )}
                    {toolCall.status === 'pending' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            Pending approval
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <ToolCallStatusIcon status={toolCall.status} />
                    {isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-400" />
                    ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-400" />
                    )}
                </div>
            </button>

            {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-gray-200 dark:border-neutral-700 space-y-2">
                    {toolCall.args && Object.keys(toolCall.args).length > 0 && (
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 font-semibold">
                                Input
                            </p>
                            <pre className="text-[11px] bg-white dark:bg-neutral-800 p-2 rounded border border-gray-200 dark:border-neutral-700 overflow-x-auto text-gray-600 dark:text-gray-400">
                                {JSON.stringify(toolCall.args, null, 2)}
                            </pre>
                        </div>
                    )}

                    {toolCall.status === 'completed' && toolCall.result !== undefined && (
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-green-500 mb-1 font-semibold">
                                Result
                            </p>
                            <pre className="text-[11px] bg-green-50 dark:bg-green-900/20 p-2 rounded border border-green-200 dark:border-green-800 overflow-x-auto text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                {formatResult(toolCall.result)}
                            </pre>
                        </div>
                    )}

                    {toolCall.status === 'pending' && (
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1 font-semibold">
                                Awaiting Confirmation
                            </p>
                            <pre className="text-[11px] bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-200 dark:border-amber-800 overflow-x-auto text-amber-700 dark:text-amber-300 whitespace-pre-wrap">
                                {pendingMessage}
                            </pre>
                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    onClick={() => void handleResolve('approve')}
                                    disabled={isResolving}
                                    className="px-2.5 py-1 text-[11px] rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isResolving ? 'Processing...' : 'Approve'}
                                </button>
                                <button
                                    onClick={() => void handleResolve('reject')}
                                    disabled={isResolving}
                                    className="px-2.5 py-1 text-[11px] rounded bg-gray-200 text-gray-700 dark:bg-neutral-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    )}

                    {toolCall.status === 'error' && toolCall.error && (
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-red-500 mb-1 font-semibold">
                                Error
                            </p>
                            <pre className="text-[11px] bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800 overflow-x-auto text-red-600 dark:text-red-400">
                                {toolCall.error}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
})

ToolCallItem.displayName = 'ToolCallItem'

const ToolCallsDisplay = memo(({
    sessionId: _sessionId,
    messageId: _messageId,
    toolCalls,
    onResolvePendingTool,
}: {
    sessionId: string
    messageId: string
    toolCalls: ToolCall[]
    onResolvePendingTool?: (toolCall: ToolCall, decision: 'approve' | 'reject') => Promise<void>
}) => {
    if (!toolCalls || toolCalls.length === 0) return null

    return (
        <div className="mb-3 space-y-2">
            {toolCalls.map((toolCall) => (
                <ToolCallItem
                    key={toolCall.id}
                    toolCall={toolCall}
                    onResolve={onResolvePendingTool}
                />
            ))}
        </div>
    )
})

ToolCallsDisplay.displayName = 'ToolCallsDisplay'

// =============================================================================
// TEXT TO SPEECH
// =============================================================================

const ChatBubbleTextToSpeech = memo(({ text = '' }: { text: string }) => {
    const [textSpeech, setTextSpeech] = useAtom(textToSpeechSettingsAtom)
    const { speak, stop, isSpeaking } = useTextToSpeech()

    const onPlay = () => {
        if (isSpeaking) {
            stop()
        } else {
            speak({
                text,
                lang: textSpeech?.language || 'en-US',
                rate: textSpeech?.rate || 1,
                pitch: textSpeech?.pitch || 1,
                voiceURI: textSpeech?.voiceURI,
            })
        }
    }

    if (!isSpeaking) return null

    return (
        <div className="flex items-center gap-2 mt-2">
            <button
                onClick={onPlay}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg transition-colors"
            >
                <AiFillPauseCircle className="text-sm" />
                Stop speaking
            </button>
            <button
                onClick={() => setTextSpeech({ ...textSpeech, show: true })}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
                Voice settings
            </button>
        </div>
    )
})

ChatBubbleTextToSpeech.displayName = 'ChatBubbleTextToSpeech'

export default ChatBubble

